
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Router } from '@angular/router';
import { API_URL } from '../../../../../config';

export interface LoginLogsDTO {
  id?: string;
  gsiType?: string;
  loginTime: number;
  loginUserId: string;
  ipAddress?: string;
  location?: string;
  userName?: string;
  logActions?: any[];
  logChatHistory?: any[];
}

export interface LogActionDTO {
  id: string;
  promptId: string;
  actionName: string;
  timestamp: number;
}

export interface LogChatHistoryDTO {
  id: string;
  promptId: string;
  chathistoryId: string;
  timestamp: number;
}

export interface PromptMemory {
  id: string;
  prompt: string;
  name: string;
  consultantName: string;
  presentRole: string;
  interviewingRole: string;
  interviewingClient: string;
  presentClient: string;
  previousClient: string;
  interviewType: string;
  jobDescription: string;
  timestamp: number;
}

export interface LogActionDetail {
  id: string;
  promptId: string;
  actionName: string;
  timestamp: number;
  promptMemory: PromptMemory;
}

export interface ChatHistoryDetail {
  id: string;
  promptId: string;
  question: string;
  answer: string;
  timestamp: number;
  s3Url: string | null;
}

export interface LogChatHistoryDetail {
  id: string;
  promptId: string;
  chathistoryId: string;
  timestamp: number;
  chatHistory: ChatHistoryDetail;
}

export interface LoginLogDetailsDTO {
  id: string;
  gsiType: string;
  loginTime: number;
  logoutTime?: number;
  loginUserId: string;
  ipAddress: string;
  location: string;
  device: string;
  userName: string;
  logActions: LogActionDetail[];
  logChatHistory: LogChatHistoryDetail[];
}

@Injectable({
  providedIn: 'root'
})
export class LoginLogsService {


  constructor(private http: HttpClient, private router: Router) { }
  baseUrl = `${API_URL}/login-logs`;
  createLoginLog(loginLogsDTO: LoginLogsDTO): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/save`, loginLogsDTO, this.getHttpOptions());
  }

  updateLoginLog(id: string, loginLogsDTO: LoginLogsDTO): Observable<any> {
    return this.http.put<any>(`${this.baseUrl}/update`, loginLogsDTO, {
      params: { id }
    });
  }

  updateLoginLogById(id: string, logoutTime: number): Observable<any> {
    const body = { logoutTime };
    return this.http.put<any>(`${this.baseUrl}/update/${id}`, body, this.getHttpOptions());
  }


  logUpdate(loginLogsId: string, onComplete?: () => void): void {
    const logoutTime = Date.now();
    this.updateLoginLogById(loginLogsId, logoutTime).subscribe({
      next: () => {
        if (onComplete) {
          onComplete();
        }
        this.router.navigate(['/auth/sign-in']);
      },
      error: (err) => {
        console.error('Error updating login log:', err);
        if (onComplete) {
          onComplete();
        }
        this.router.navigate(['/auth/sign-in']);
      }
    });
  }


  getLoginLogById(id: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/${id}`, this.getHttpOptions());
  }

  getLoginLogDetailsById(id: string): Observable<LoginLogDetailsDTO> {
    return this.http.get<LoginLogDetailsDTO>(`${this.baseUrl}/details-dto/${id}`, this.getHttpOptions());
  }
  getAllLoginLogs(page: number, size: number): Observable<any> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());

    return this.http.get<any>(`${this.baseUrl}/all`, {
      params: params,
      ...this.getHttpOptions()
    });
  }


  getLoginLogsByLoginTime(loginTime: number): Observable<any[]> {
    return this.http.get<any[]>(`${this.baseUrl}/find-by-login-time/${loginTime}`, this.getHttpOptions());
  }

  private getHttpOptions() {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return { headers };
  }

  searchLoginLogs(criteria: any, page: number = 0, size: number = 10) {
    const params: any = { ...criteria, page, size };
    return this.http.get<any>(`${this.baseUrl}/search`, { params });
  }

  appendLogAction(loginLogId: string, logAction: LogActionDTO): Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}/${loginLogId}/append-log-action`,
      logAction,
      this.getHttpOptions()
    );
  }

  appendLogChatHistory(loginLogId: string, logChatHistory: LogChatHistoryDTO): Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}/${loginLogId}/append-log-chat-history`,
      logChatHistory,
      this.getHttpOptions()
    );
  }

  generateUUID(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }
}
